import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';

interface FilterCriteria {
  categories: string[];
  tags: string[];
  dateRange: { start: Date | null; end: Date | null };
  uploadedBy: string[];
  fileTypes: string[];
}

interface DocumentFilterSidebarProps {
  open: boolean;
  filters: FilterCriteria;
  onFilterChange: (filters: FilterCriteria) => void;
  onClose: () => void;
}

const availableCategories = ['Finance', 'Planning', 'Analytics', 'HR', 'Marketing', 'Engineering'];
const availableTags = ['quarterly', 'report', 'strategy', 'roadmap', 'analytics', 'dashboard', '2024', 'confidential'];
const availableFileTypes = ['pdf', 'docx', 'xlsx', 'pptx'];
const availableUsers = ['John Doe', 'Jane Smith', 'Mike Johnson', 'Sarah Williams'];

const DocumentFilterSidebar: React.FC<DocumentFilterSidebarProps> = ({
  open,
  filters,
  onFilterChange,
  onClose,
}) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState<FilterCriteria>(filters);

  const handleCategoryChange = (category: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newCategories = event.target.checked
      ? [...localFilters.categories, category]
      : localFilters.categories.filter((c) => c !== category);
    setLocalFilters({ ...localFilters, categories: newCategories });
  };

  const handleTagChange = (tag: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newTags = event.target.checked
      ? [...localFilters.tags, tag]
      : localFilters.tags.filter((t) => t !== tag);
    setLocalFilters({ ...localFilters, tags: newTags });
  };

  const handleFileTypeChange = (fileType: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFileTypes = event.target.checked
      ? [...localFilters.fileTypes, fileType]
      : localFilters.fileTypes.filter((ft) => ft !== fileType);
    setLocalFilters({ ...localFilters, fileTypes: newFileTypes });
  };

  const handleUserChange = (user: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newUsers = event.target.checked
      ? [...localFilters.uploadedBy, user]
      : localFilters.uploadedBy.filter((u) => u !== user);
    setLocalFilters({ ...localFilters, uploadedBy: newUsers });
  };

  const handleDateRangeChange = (field: 'start' | 'end') => (date: Date | null) => {
    setLocalFilters({
      ...localFilters,
      dateRange: {
        ...localFilters.dateRange,
        [field]: date,
      },
    });
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
    onClose();
  };

  const handleClearAll = () => {
    const clearedFilters: FilterCriteria = {
      categories: [],
      tags: [],
      dateRange: { start: null, end: null },
      uploadedBy: [],
      fileTypes: [],
    };
    setLocalFilters(clearedFilters);
  };

  const totalActiveFilters =
    localFilters.categories.length +
    localFilters.tags.length +
    localFilters.uploadedBy.length +
    localFilters.fileTypes.length +
    (localFilters.dateRange.start || localFilters.dateRange.end ? 1 : 0);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 320,
          boxSizing: 'border-box',
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{t('documents.filters')}</Typography>
          <Box>
            {totalActiveFilters > 0 && (
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleClearAll}
                sx={{ mr: 1 }}
              >
                {t('common.clearAll')}
              </Button>
            )}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {totalActiveFilters > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t(totalActiveFilters === 1 ? 'documents.activeFilter' : 'documents.activeFilters', {
                count: totalActiveFilters,
              })}
            </Typography>
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          {/* Categories */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{t('documents.categories')}</Typography>
              {localFilters.categories.length > 0 && (
                <Chip label={localFilters.categories.length} size="small" sx={{ ml: 1 }} />
              )}
            </AccordionSummary>
            <AccordionDetails>
              <FormControl component="fieldset">
                <FormGroup>
                  {availableCategories.map((category) => (
                    <FormControlLabel
                      key={category}
                      control={
                        <Checkbox
                          checked={localFilters.categories.includes(category)}
                          onChange={handleCategoryChange(category)}
                        />
                      }
                      label={category}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </AccordionDetails>
          </Accordion>

          {/* Tags */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{t('documents.tags')}</Typography>
              {localFilters.tags.length > 0 && (
                <Chip label={localFilters.tags.length} size="small" sx={{ ml: 1 }} />
              )}
            </AccordionSummary>
            <AccordionDetails>
              <FormControl component="fieldset">
                <FormGroup>
                  {availableTags.map((tag) => (
                    <FormControlLabel
                      key={tag}
                      control={
                        <Checkbox
                          checked={localFilters.tags.includes(tag)}
                          onChange={handleTagChange(tag)}
                        />
                      }
                      label={tag}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </AccordionDetails>
          </Accordion>

          {/* File Types */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{t('documents.fileTypes')}</Typography>
              {localFilters.fileTypes.length > 0 && (
                <Chip label={localFilters.fileTypes.length} size="small" sx={{ ml: 1 }} />
              )}
            </AccordionSummary>
            <AccordionDetails>
              <FormControl component="fieldset">
                <FormGroup>
                  {availableFileTypes.map((fileType) => (
                    <FormControlLabel
                      key={fileType}
                      control={
                        <Checkbox
                          checked={localFilters.fileTypes.includes(fileType)}
                          onChange={handleFileTypeChange(fileType)}
                        />
                      }
                      label={fileType.toUpperCase()}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </AccordionDetails>
          </Accordion>

          {/* Uploaded By */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{t('documents.uploadedBy')}</Typography>
              {localFilters.uploadedBy.length > 0 && (
                <Chip label={localFilters.uploadedBy.length} size="small" sx={{ ml: 1 }} />
              )}
            </AccordionSummary>
            <AccordionDetails>
              <FormControl component="fieldset">
                <FormGroup>
                  {availableUsers.map((user) => (
                    <FormControlLabel
                      key={user}
                      control={
                        <Checkbox
                          checked={localFilters.uploadedBy.includes(user)}
                          onChange={handleUserChange(user)}
                        />
                      }
                      label={user}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </AccordionDetails>
          </Accordion>

          {/* Date Range */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{t('documents.dateRange')}</Typography>
              {(localFilters.dateRange.start || localFilters.dateRange.end) && (
                <Chip label={t('search.active')} size="small" sx={{ ml: 1 }} />
              )}
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <DatePicker
                  label={t('documents.startDate')}
                  value={localFilters.dateRange.start}
                  onChange={handleDateRangeChange('start')}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
                <DatePicker
                  label={t('documents.endDate')}
                  value={localFilters.dateRange.end}
                  onChange={handleDateRangeChange('end')}
                  minDate={localFilters.dateRange.start || undefined}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </LocalizationProvider>

        <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
          <Button variant="outlined" fullWidth onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" fullWidth onClick={handleApplyFilters}>
            {t('documents.applyFilters')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default DocumentFilterSidebar;
